import { forwardRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { SnackbarContent, CustomContentProps, useSnackbar } from "notistack";
import { Button, Divider, Paper, Typography } from "@mui/material";

import { useSignalRContext } from "../Contexts/SignalRContext";
import { NotificationType } from "../../types/Notifications/NotificationBase";

export default function GlobalNotificationListener() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { connection } = useSignalRContext();

  useEffect(() => {
    if (!connection) return;

    const handleGlobalNotification = (
      rawMessage: string,
      notificationType: string,
      callerUsername: string,
      notificationTimestamp: string
    ) => {
      if (
        notificationType !== "StoppageSetAsAvailableNotification" &&
        notificationType !== "StoppageSetAsUnavailableNotification"
      ) return;

      const safeMessage = DOMPurify.sanitize(rawMessage);

      let notification: NotificationType | null = null;

      try {
        notification = JSON.parse(safeMessage) as NotificationType;
      } catch {
        // not JSON, leave as null
      }

      enqueueSnackbar(safeMessage, {
        persist: true,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        content: (key, message) => (
          <CustomNotification
            id={key}
            message={notification ? renderNotification(notification, callerUsername) : message}
            notificationTimestamp={notificationTimestamp}
            persist={true}
            style={{}}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            variant={"default"}
            hideIconVariant={false}
            iconVariant={{}}
          />
        ),
      });
    };

    connection.on("GlobalNotificationAsync", handleGlobalNotification);

    return () => {
      connection.off("GlobalNotificationAsync", handleGlobalNotification);
    };
  }, [connection, enqueueSnackbar, closeSnackbar]);

  const CustomNotification = forwardRef<
    HTMLDivElement,
    CustomContentProps & { notificationTimestamp?: string }
  >((props, ref) => {
    const { id, message, notificationTimestamp, ...other } = props;
    const { closeSnackbar } = useSnackbar();

    return (
      <SnackbarContent ref={ref} {...other}>
        <Paper elevation={6} className="flex flex-col p-4 w-[360px]">
          <Typography className="text-xl font-bold mb-2 italic text-theme-text" variant="h6">
            Notification
          </Typography>

          <Divider />
          <Typography className="text-theme-text mt-2" variant="body1">{message}</Typography>
          <div className="flex justify-between items-center mt-4">
            {
              notificationTimestamp && (
                <Typography className="text-theme-text-muted italic text-sm" variant="body2">
                  {notificationTimestamp}
                </Typography>
              )
            }
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => closeSnackbar(id)}
            >
              Dismiss
            </Button>
          </div>
        </Paper>
      </SnackbarContent>
    );
  });

  return null;
}

const renderNotification = (notification: NotificationType, callerUsername?: string) => {
  switch (notification.type) {
    case "StoppageSetAsAvailableNotification":
      return (
        <Typography className="text-theme-text text-lg">
          The status of plant{" "}
          <span className="font-bold" style={{ color: notification.plantColour }}>
            {notification.plantRef}
          </span>{" "}
          has been set as{" "}
          <span className="font-bold text-green-500">Up</span>
          {callerUsername && (
            <>
              <span> by </span><span className="font-bold">{callerUsername}</span>
            </>
          )}{"."}
        </Typography>
      );

    case "StoppageSetAsUnavailableNotification":
      return (
        <Typography className="text-theme-text text-lg">
          Status of plant{" "}
          <span className='font-bold' style={{ color: notification.plantColour }}>
            {notification.plantRef}
          </span>{" "}
          has been set as{" "}
          <span className="font-bold" style={{ color: notification.statusColour }}>
            {notification.status}
          </span>{" "}
          {callerUsername && (
            <>
              <span> by </span><span className="font-bold">{callerUsername}</span>
            </>
          )}{"."}
        </Typography>
      );

    default:
      return <Typography>You have a new notification.</Typography>;
  }
};